import { Fragment } from 'react';
import Icon from './Icon';
import PlayerChip from './PlayerChip';
import { StatusBadge, cx } from './primitives';
import { useIsMobile } from './useMedia';

/**
 * DataTable — egdl-row + hairlines on a CSS grid; caps-14 headers.
 * Below 768 px it stacks into label/value cards (first column is the card title).
 *
 * columns: [{ key, label, w?: '1fr', align?: 'right', strong?, muted?,
 *             type?: 'status' | 'players' | 'custom', render?: (row) => node, tag?: bool, sortable?: bool }]
 * rows: objects; row.onClick makes the row a link-like button (Enter / click).
 * sort + onSort: a sortable column's header is a button that calls onSort(column key); the table
 * only shows the order (sort = { key, dir: 'asc' | 'desc' }), the caller sorts the rows.
 */
export default function DataTable({ columns, rows, caption = 'Table', dense, emptyText = 'Nothing to show', getKey, forceMobile, sort, onSort }) {
    const isMobile = useIsMobile();
    const mobile = forceMobile ?? isMobile;
    const grid = columns.map((c) => c.w || '1fr').join(' ');

    if (!rows.length) return <div className="eg-table__empty">{emptyText}</div>;

    const cell = (c, r) => {
        const v = r[c.key];
        if (c.render) return c.render(r);
        if (c.type === 'status') return v ? <StatusBadge status={v} /> : <span className="eg-table__text eg-table__text--muted">—</span>;
        if (c.type === 'players') {
            if (Array.isArray(v) && v.length) {
                return (
                    <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 12px', minWidth: 0 }}>
                        {c.withText ? <span className="eg-small eg-secondary">with</span> : null}
                        {v.map((p) => <PlayerChip key={p.slot} variant={c.tag ? 'tag' : 'compact'} slot={p.slot} name={p.name} guest={p.guest} showGuestNote={false} />)}
                    </span>
                );
            }
            return <span className="eg-table__text eg-table__text--muted">{v || 'Solo'}</span>;
        }
        return (
            <span className={cx('eg-table__text', c.strong && 'eg-table__text--strong', c.muted && 'eg-table__text--muted')} title={typeof v === 'string' ? v : undefined}>
                {v ?? '—'}
            </span>
        );
    };

    const activate = (r) => (e) => {
        if (!r.onClick) return;
        if (e.type === 'click' || e.key === 'Enter' || e.key === ' ') {
            if (e.key === ' ') e.preventDefault();
            r.onClick();
        }
    };

    if (mobile) {
        const statusCol = columns.find((c) => c.type === 'status');
        return (
            <div role="list" aria-label={caption} className="eg-cards">
                {rows.map((r, i) => (
                    <div
                        key={getKey ? getKey(r, i) : i}
                        role="listitem"
                        className={cx('eg-cards__item', r.onClick && 'eg-cards__item--click')}
                        tabIndex={r.onClick ? 0 : undefined}
                        onClick={r.onClick ? activate(r) : undefined}
                        onKeyDown={r.onClick ? activate(r) : undefined}
                    >
                        <div className="eg-cards__top">
                            <span className="eg-cards__title">{columns[0].render ? columns[0].render(r) : r[columns[0].key]}</span>
                            {statusCol && r[statusCol.key] ? <StatusBadge status={r[statusCol.key]} /> : null}
                        </div>
                        <div className="eg-cards__grid">
                            {columns.slice(1).filter((c) => c.type !== 'status' && !c.hideOnMobile).map((c) => (
                                <div key={c.key} className="eg-cards__cell">
                                    <span className="eg-caps-14 eg-secondary">{c.label}</span>
                                    {c.type === 'players' || c.render ? cell(c, r) : <span className="eg-cards__value">{r[c.key] ?? '—'}</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div role="table" aria-label={caption} className="eg-table">
            <div role="row" className="eg-table__row eg-table__row--head" style={{ gridTemplateColumns: grid }}>
                {columns.map((c) => {
                    const sortable = c.sortable && onSort;
                    const on = sortable && sort?.key === c.key;
                    return (
                        <span
                            key={c.key}
                            role="columnheader"
                            aria-sort={sortable ? (on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none') : undefined}
                            className="eg-table__th"
                            style={{ justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start' }}
                        >
                            {sortable ? (
                                <button type="button" className={cx('eg-table__sort', on && 'eg-table__sort--on')} onClick={() => onSort(c.key)} title={typeof c.label === 'string' ? `Sort by ${c.label.toLowerCase()}` : undefined}>
                                    <span className="eg-table__sort-label">{c.label}</span>
                                    <Icon name={on ? (sort.dir === 'asc' ? 'sortAsc' : 'sortDesc') : 'sortBoth'} size={16} className="eg-table__sort-icon" />
                                </button>
                            ) : c.label}
                        </span>
                    );
                })}
            </div>
            {rows.map((r, i) => (
                <div
                    key={getKey ? getKey(r, i) : i}
                    role="row"
                    className={cx('eg-table__row', 'eg-table__row--body', r.onClick && 'eg-table__row--click')}
                    style={{ gridTemplateColumns: grid, minHeight: dense ? 44 : 56 }}
                    tabIndex={r.onClick ? 0 : undefined}
                    onClick={r.onClick ? activate(r) : undefined}
                    onKeyDown={r.onClick ? activate(r) : undefined}
                >
                    {columns.map((c) => (
                        <Fragment key={c.key}>
                            <div role="cell" className="eg-table__td" style={{ justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start' }}>
                                {cell(c, r)}
                            </div>
                        </Fragment>
                    ))}
                </div>
            ))}
        </div>
    );
}
